import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Home, Building2, Factory, ShieldCheck, ArrowRight, Sun, Zap } from 'lucide-react';

const Services = () => {
  // Trace currently focused service card
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const services = [
    {
      icon: Home,
      title: 'Residential Solar',
      description: 'Lower monthly utility expenses seamlessly and shield your home from surging power pricing spikes permanently.',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=600&auto=format&fit=crop&q=80',
    },
    {
      icon: Building2,
      title: 'Commercial Solar',
      description: 'Megawatt-grade industrial power infrastructure tailored explicitly to reduce operational overheads and satisfy compliance.',
      image: 'https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=600&auto=format&fit=crop&q=80',
    },
    {
      icon: Factory,
      title: 'Industrial Plants',
      description: 'Heavy-duty solar engineering systems configured specifically for large-scale operations to mitigate massive grid billing.',
      image: 'https://images.unsplash.com/photo-1548345680-f5475ea5df84?w=600&auto=format&fit=crop&q=80',
    },
    {
      icon: ShieldCheck,
      title: 'Matrix Storage Core',
      description: 'Smart automation batteries storing daytime surplus for smooth backup power distribution during local grid outages.',
      image: 'https://images.unsplash.com/photo-1538094463633-23136790dbb9?w=600&auto=format&fit=crop&q=80',
    },
  ];

  return (
    // Compact Padding for Zero-Scroll fit on standard displays
    <section id="services" className="py-16 sm:py-20 bg-[#F4F7FA] relative overflow-hidden border-y border-slate-200/50 font-sans">
      
      {/* 🌟 Ambient Subtle Glows 🌟 */}
      <div className="absolute top-20 right-0 w-80 h-80 bg-[#F7941D]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-10 left-0 w-96 h-96 bg-[#0D3F7F]/10 rounded-full blur-[130px] pointer-events-none" />

      {/* Grid Pattern Background */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ backgroundImage: `radial-gradient(#0D3F7F 1.5px, transparent 1.5px)`, backgroundSize: '24px 24px' }} 
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🌟 1. SECTION HEADER 🌟 */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 sm:mb-16 gap-5">
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-left"
          >
            <div className="inline-flex items-center gap-1.5 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3 py-1 rounded-full mb-3 shadow-sm">
              <Sun className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[10px] font-bold tracking-[0.15em] text-[#0D3F7F] uppercase">
                Premium Solutions
              </span>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#0F172A] tracking-tight leading-tight">
              Our Solar <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent drop-shadow-sm">Ecosystem</span>
            </h2>
          </motion.div>
          
          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="text-slate-500 max-w-md font-medium text-[13px] sm:text-[14px] leading-relaxed border-l-2 border-[#F7941D] pl-4"
          >
            Custom tailored structural power engineering designed dynamically for extreme sustainability and energy reduction across all sectors.
          </motion.p>
        </div>

        {/* 🌟 2. INTERACTIVE CARDS MESH 🌟 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
          {services.map((service, index) => {
            const isAnyCardHovered = hoveredIndex !== null;
            const isThisCardHovered = hoveredIndex === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                animate={{
                  scale: isThisCardHovered ? 1.02 : 1,
                  opacity: isAnyCardHovered && !isThisCardHovered ? 0.6 : 1,
                  y: isThisCardHovered ? -8 : 0
                }}
                className={`bg-white rounded-[1.5rem] overflow-hidden transition-all duration-400 group flex flex-col h-full relative ${
                  isThisCardHovered 
                    ? 'shadow-[0_20px_40px_rgba(13,63,127,0.1)] border-transparent' 
                    : 'border border-slate-200/80 shadow-sm'
                }`}
              >
                
                {/* ── IMAGE SECTION ── */}
                <div className="h-44 sm:h-48 overflow-hidden relative">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" 
                    loading="lazy"
                  />
                  {/* Premium Gradient overlay on image */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/90 via-[#0F172A]/20 to-transparent pointer-events-none" />
                  
                  {/* Floating Tech Icon inside Image */}
                  <div className="absolute bottom-4 right-4 w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white group-hover:bg-[#F7941D] group-hover:border-[#F7941D] transition-all duration-300 shadow-lg">
                    <service.icon className="w-4.5 h-4.5" />
                  </div>

                  {/* Absolute Title inside image for cleaner look */}
                  <h3 className="absolute bottom-4 left-5 right-16 text-[16px] sm:text-[18px] font-black text-white tracking-tight leading-tight group-hover:text-[#FFC107] transition-colors duration-300">
                    {service.title}
                  </h3>
                </div>

                {/* ── CONTENT SECTION ── */}
                <div className="p-5 sm:p-6 flex flex-col flex-1 justify-between bg-white relative z-10">
                  
                  <p className="text-slate-500 font-medium text-[12px] sm:text-[13px] leading-relaxed mb-6">
                    {service.description}
                  </p>
                  
                  {/* Action Link */}
                  <a href="#contact" className="inline-flex items-center gap-2 text-[12px] font-black tracking-widest text-[#0D3F7F] uppercase transition-all duration-300 group-hover:text-[#F7941D]">
                    <span>Discover More</span>
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </a>
                </div>

                {/* ── BOTTOM ACCENT LINE (Magic Line) ── */}
                <div className="absolute bottom-0 left-0 right-0 h-1.5 w-0 bg-gradient-to-r from-[#F7941D] to-[#FFC107] group-hover:w-full transition-all duration-500 ease-out z-20" />
                
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default Services;